import type { Campaign, CampaignListResult, DescubrirCampaignsResult } from '@/types/campaigns';

/**
 * Formato de las filas de gasto para la pantalla.
 *
 * Dos decisiones que valen para todo el módulo:
 *  - el dinero se pinta como NÚMERO en formato es-CO + el código ISO («25.000 COP», no «$25.000»):
 *    `COP`, `MXN` y `USD` comparten el símbolo «$», así que el símbolo no dice nada y el código sí;
 *  - el gasto SIEMPRE dice «acumulado»: `gastoActual` es lo gastado en el periodo que el admin
 *    carga a mano, no el gasto del día (y todavía no lo trae la Meta Ads API).
 */

const LOCALE = 'es-CO';

/** Alcance de una fila: el gasto de un anuncio concreto o el de la campaña entera. */
export type CampaignAlcance = 'anuncio' | 'campana';

/** Filtro de la pantalla por alcance (`'todos'` = sin filtro). */
export type CampaignAlcanceFiltro = 'todos' | CampaignAlcance;

/** ¿La fila es el gasto de un anuncio o el de la campaña? (lo decide `metaAdId`). */
export function campaignAlcance(campaign: Campaign): CampaignAlcance {
  return campaign.metaAdId ? 'anuncio' : 'campana';
}

/** «Anuncio» / «Campaña», para el badge del alcance. */
export function alcanceLabel(campaign: Campaign): string {
  return campaignAlcance(campaign) === 'anuncio' ? 'Anuncio' : 'Campaña';
}

/** «25.000» / «22.500,5» (es-CO). */
export function formatNumber(value: number): string {
  return value.toLocaleString(LOCALE, { maximumFractionDigits: 2 });
}

/** «25.000 COP» / «1.250.500,5 USD». */
export function formatMoney(value: number, moneda: string): string {
  const code = moneda.trim().toUpperCase() || 'COP';
  return `${formatNumber(value)} ${code}`;
}

/** «25.000 COP acumulado»: el gasto que ya trae la fila. */
export function gastoAcumuladoLabel(campaign: Campaign): string {
  return `${formatMoney(campaign.gastoActual, campaign.moneda)} acumulado`;
}

/** «3 filas de gasto» / «1 fila de gasto» (plural explícito: en es no se pluraliza solo). */
export function countLabel(count: number, singular: string, plural: string): string {
  return `${formatNumber(count)} ${count === 1 ? singular : plural}`;
}

/**
 * Los ids de Meta de la fila, tal como se leen en la tabla.
 *
 * Con `metaAdId` la fila es el gasto de ESE anuncio (y `metaCampaignId`, si viene, solo agrupa);
 * sin `metaAdId` es gasto a nivel campaña, donde el id de la campaña es obligatorio.
 */
export function describeCampaignIds(campaign: Campaign): string {
  if (campaign.metaAdId && campaign.metaCampaignId) {
    return `Anuncio ${campaign.metaAdId} · Campaña ${campaign.metaCampaignId}`;
  }
  if (campaign.metaAdId) return `Anuncio ${campaign.metaAdId}`;
  if (campaign.metaCampaignId) return `Campaña ${campaign.metaCampaignId}`;
  return 'Sin id de Meta (no debería pasar: el backend exige uno de los dos)';
}

/**
 * ¿El nombre sigue siendo el provisorio que pone `descubrir` («Anuncio <id>» / «Campaña <id>»)?
 *
 * Sirve para avisar en la tabla de que esa fila todavía no tiene nombre de verdad; el admin la
 * renombra al cargar el gasto.
 */
export function hasAutoNombre(campaign: Campaign): boolean {
  const id = campaign.metaAdId ?? campaign.metaCampaignId;
  if (!id) return false;
  return campaign.nombre === `Anuncio ${id}` || campaign.nombre === `Campaña ${id}`;
}

/** Todo lo que se puede buscar de una fila (el `buscar` del backend mira lo mismo). */
export function campaignSearchText(campaign: Campaign): string {
  return [campaign.nombre, campaign.metaAdId ?? '', campaign.metaCampaignId ?? '', campaign.moneda]
    .join(' ')
    .toLowerCase();
}

/**
 * Búsqueda y filtro de alcance sobre las filas YA cargadas.
 *
 * El reparto con el servidor es deliberado (igual que en Órdenes y CRM): `activo` viaja al backend
 * (es un query param real y de él dependen `total` y `gastoTotal`), mientras el buscador y el
 * alcance se resuelven acá. El buscador no se manda porque el endpoint NO pagina —`items` es el
 * catálogo entero— y escribir letra a letra no debe disparar una petición por tecla.
 */
export function filterCampaigns(
  campaigns: Campaign[],
  options: { search?: string; alcance?: CampaignAlcanceFiltro } = {},
): Campaign[] {
  const needle = options.search?.trim().toLowerCase() ?? '';
  const alcance = options.alcance ?? 'todos';

  return campaigns.filter((campaign) => {
    if (alcance !== 'todos' && campaignAlcance(campaign) !== alcance) return false;
    if (needle.length === 0) return true;
    return campaignSearchText(campaign).includes(needle);
  });
}

/** «2 por anuncio · 1 a nivel campaña» (la mezcla de granularidades del catálogo). */
export function describeAlcanceMix(anuncios: number, campanias: number): string {
  const partes: string[] = [];
  if (anuncios > 0) partes.push(countLabel(anuncios, 'por anuncio', 'por anuncio'));
  if (campanias > 0) partes.push(countLabel(campanias, 'a nivel campaña', 'a nivel campaña'));
  return partes.join(' · ');
}

/**
 * Resumen del encabezado: cuántas filas hay, cuánto gasto acumulan y de qué tipo son.
 *
 * `total` y `gastoTotal` los calcula el backend sobre las filas que cumplen el filtro de `activo`;
 * las filas de `items` son todas (el endpoint no pagina), así que el desglose por alcance sale del
 * mismo conjunto.
 */
export function summarizeCampaigns(result: CampaignListResult): string {
  if (result.total === 0) return 'Todavía no hay gasto registrado';

  const anuncios = result.items.filter((campaign) => campaignAlcance(campaign) === 'anuncio').length;
  const campanias = result.items.length - anuncios;
  const moneda = result.items[0]?.moneda ?? 'COP';

  const partes = [
    countLabel(result.total, 'fila de gasto', 'filas de gasto'),
    `${formatMoney(result.gastoTotal, moneda)} acumulado`,
  ];

  if (result.items.length > 0) partes.push(describeAlcanceMix(anuncios, campanias));

  return partes.join(' · ');
}

/** Fecha de la última vez que se tocó el gasto («Actualizado el 16 sept 2026»). */
export function describeUpdatedAt(formatted: string): string {
  return formatted === '—' ? 'Sin fecha de actualización' : `Actualizado el ${formatted}`;
}

/**
 * Lo que se cuenta cuando termina «Descubrir anuncios»: cuántas filas se crearon (con gasto 0),
 * cuántas ya existían y qué se vio en los referrals de las conversaciones.
 */
export function summarizeDescubrir(result: DescubrirCampaignsResult): string {
  const partes: string[] = [];

  partes.push(
    result.creadas === 0
      ? 'No había filas nuevas por crear'
      : `Se crearon ${countLabel(result.creadas, 'fila nueva', 'filas nuevas')} con gasto 0`,
  );

  if (result.existentes > 0) {
    partes.push(countLabel(result.existentes, 'fila ya existía', 'filas ya existían'));
  }

  partes.push(
    `se vieron ${countLabel(result.anunciosVistos, 'anuncio', 'anuncios')} y ${countLabel(
      result.campaniasVistas,
      'campaña',
      'campañas',
    )} en las conversaciones`,
  );

  return `${partes.join(', ')}.`;
}
